import type { FunctionComponent, ReactNode } from 'react'
import { useEffect } from 'react'
import { type AuthenticationDomainRouteName } from '@retailer-platform/domain-authentication'
import { useLocation } from 'react-router'
import { DashRedirect } from '../../../legacy/dash-components/dash-redirect/DashRedirect'
import { useCurrentAccountContext } from '../../../utils/current-account/CurrentAccountContext'
import { type DashRouteName } from '../../../utils/routing/routes'
import { consumeStoredRedirectUrl, storeRedirectUrl } from '../utils/loggedIn.utils'

interface Props {
  children: ReactNode
}

/* This component checks if the user is signed in and allowed to use this part of the app.
 * If not, it remembers where they wanted to go, sends them to log in, and brings them back afterward.
 * If everything is fine, it lets them see the page.
 */
export const NotLoggedInRedirect: FunctionComponent<Props> = ({ children }) => {
  const { pathname, search, hash } = useLocation()
  const { account } = useCurrentAccountContext()

  // If we are not logged in or don't have the required policy, redirect to login
  // We store the defaultRedirectOverride for later when the user logs in again
  const hasRequiredPolicy = account?.policies.viewRetailerDashboardSection
  if (!account || !hasRequiredPolicy) {
    storeRedirectUrl({ pathname, search, hash })
    return (
      <DashRedirect
        toRoute={
          'app-public-authentication-log-in' satisfies AuthenticationDomainRouteName as DashRouteName
        }
      />
    )
  }

  // If we're logged in, and there was a stored redirect, we consume it
  const target = consumeStoredRedirectUrl()

  // If anything was returned from above, we redirect to it
  if (target) return <DashRedirect to={target} />

  return children
}
